// Composer for the assistant drawer. Enter sends, Shift+Enter inserts a newline.
import { useState } from "react";
import { ActionIcon, Box, Group, Textarea } from "@mantine/core";
import { IconArrowUp, IconPlayerStopFilled } from "@tabler/icons-react";
import {
  HeroTraceOverlay,
  heroEntranceClasses,
} from "@/components/chat/HeroTraceOverlay";
import { useAssistantName } from "./assistantBrand";

interface Props {
  onSend: (text: string) => void;
  onStop: () => void;
  isStreaming: boolean;
  disabled?: boolean;
  /** Empty-transcript state: plays the one-shot border-trace dot. */
  hero?: boolean;
}

export function AssistantInput({ onSend, onStop, isStreaming, disabled, hero }: Props) {
  const [value, setValue] = useState("");
  const assistantName = useAssistantName();

  const submit = () => {
    const text = value.trim();
    if (!text || isStreaming || disabled) return;
    onSend(text);
    setValue("");
  };

  return (
    <Group gap="xs" align="flex-end" wrap="nowrap">
      {/* Host wrapper exists in BOTH states (stable slot for the textarea);
          only the trace overlay inside it is hero-conditional. */}
      <Box className={heroEntranceClasses.heroTraceHost} style={{ flex: 1 }}>
        <Textarea
          autosize
          minRows={1}
          maxRows={6}
          placeholder={assistantName ? `Ask ${assistantName}…` : "Ask the assistant…"}
          value={value}
          disabled={disabled}
          onChange={(e) => setValue(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        {/* rx 5 = the textarea's default radius (4px) + the svg's 1px outset. */}
        {hero && <HeroTraceOverlay rx={5} />}
      </Box>
      {isStreaming ? (
        <ActionIcon size={36} radius="xl" color="gray" variant="filled" onClick={onStop} aria-label="Stop">
          <IconPlayerStopFilled size={16} />
        </ActionIcon>
      ) : (
        <ActionIcon
          size={36}
          radius="xl"
          variant="filled"
          onClick={submit}
          disabled={!value.trim() || disabled}
          aria-label="Send message"
        >
          <IconArrowUp size={18} />
        </ActionIcon>
      )}
    </Group>
  );
}
